/*
 * Copyright (C) 2026 - present Marek Kuzora - All Rights Reserved.
 */


import HexGroup from 'solis/type/hex/group';

import { makeTransform } from 'solis/type/hex/transform';


// The source colony's own (colony-local) hexes, mirroring cpp colony->get_hexes().
export const getSourceHexes = (colony) =>
{
  return new HexGroup(colony.getZones().keys());
};


// Pin source hex `s` onto board hex `t` at rotation `r`. JS transform applies
// rotateRight(r) then add(offset), so offset = t - s.rotateRight(r) keeps s mapped
// to t — rotation pivots the colony in place around t.
export const computeTransform = (source, target, rotation) =>
{
  return makeTransform(target.subtract(source.rotateRight(rotation)), rotation);
};


export const getPlacedHexes = (colony, transform) =>
{
  return getSourceHexes(colony).transformWithObject(transform);
};


// cpp is_colony_in_conflict — any placed hex overlaps an existing board zone.
export const isInConflict = (board, placedHexes) =>
{
  for (const hex of placedHexes)
  {
    if (board.getZone(hex) !== null)
    {
      return true;
    }
  }

  return false;
};


// cpp is_colony_adjacent — board colonies whose zones neighbour the placed hexes.
export const getAdjacentColonyIDs = (board, placedHexes) =>
{
  const ids = new Set();

  for (const hex of placedHexes.getNeighbors())
  {
    const zone = board.getZone(hex);

    if (zone !== null)
    {
      ids.add(zone.cass.colony);
    }
  }

  return [ ...ids ];
};


// Earliest-attached adjacent colony; ties broken by id so the pick stays deterministic.
export const getTargetColonyID = (board, placedHexes) =>
{
  const colonies = getAdjacentColonyIDs(board, placedHexes).map(id => board.getColony(id));

  colonies.sort((lhs, rhs) => (lhs.cass.attachDate - rhs.cass.attachDate) || (lhs.cass.id < rhs.cass.id ? -1 : 1));

  return colonies.length ? colonies[0].cass.id : null;
};


// Empty board hexes that touch a board colony — the legal docking spots for `t`.
// Group neighbors exclude the group's own hexes, so every neighbor is off-board.
export const getCandidateTargetHexes = (board) =>
{
  return new HexGroup(board.getZones().keys()).getNeighbors();
};


// Contact edges between the footprint and board zones — a placed hex bordering two
// zones counts twice, so wrapping placements outrank ones touching at a single hex.
const getContactCount = (board, placedHexes) =>
{
  let count = 0;

  for (const hex of placedHexes)
  {
    for (const neighbor of hex.getNeighbors())
    {
      if (board.getZone(neighbor) !== null)
      {
        ++count;
      }
    }
  }

  return count;
};


// Contact edges restricted to one colony: adjacencies between placed hexes and zones
// of `colonyID`.
const getColonyContactCount = (board, placedHexes, colonyID) =>
{
  let count = 0;

  for (const hex of placedHexes)
  {
    for (const neighbor of hex.getNeighbors())
    {
      const zone = board.getZone(neighbor);

      if (zone !== null && zone.cass.colony === colonyID)
      {
        ++count;
      }
    }
  }

  return count;
};


// All legal placements covering `target` — every colony hex pinned onto it at every
// rotation — deduped by footprint and ordered snuggest fit first. With a `colonyID`
// only placements touching that colony count, ordered by contact with it and ties
// broken by total board contact.
export const getPlacementTransforms = (board, colony, target, colonyID = null) =>
{
  const entries = [];
  const footprints = [];

  for (let rotation = 0; rotation < 6; ++rotation)
  {
    for (const source of getSourceHexes(colony))
    {
      const transform = computeTransform(source, target, rotation);
      const placedHexes = getPlacedHexes(colony, transform);

      const adjacent = isInConflict(board, placedHexes) ? [] : getAdjacentColonyIDs(board, placedHexes);

      if (colonyID !== null ? !adjacent.includes(colonyID) : adjacent.length === 0)
      {
        continue;
      }

      if (footprints.some(footprint => footprint.equals(placedHexes)))
      {
        continue;
      }

      const boardContact = getContactCount(board, placedHexes);
      const contact = colonyID !== null ? getColonyContactCount(board, placedHexes, colonyID) : boardContact;

      footprints.push(placedHexes);
      entries.push({ transform, contact, boardContact });
    }
  }

  entries.sort((lhs, rhs) => (rhs.contact - lhs.contact) || (rhs.boardContact - lhs.boardContact));

  return entries.map(entry => entry.transform);
};


// Hex.getNeighbors returns an iterable Set, so membership is checked with a loop.
export const isColonyRimHex = (board, candidate, colonyID) =>
{
  for (const hex of candidate.getNeighbors())
  {
    const zone = board.getZone(hex);

    if (zone !== null && zone.cass.colony === colonyID)
    {
      return true;
    }
  }

  return false;
};


// Docking hexes the colony can cover with a legal placement — the attach markers.
// Every valid footprint touches the board, so it contains a docking hex: restricting
// clicks to the candidate ring keeps every placement reachable. With a `colonyID` the
// candidates narrow to that colony's rim.
export const getPlacementTargets = (board, colony, colonyID = null) =>
{
  const candidates = getCandidateTargetHexes(board).toArray();

  const rim = colonyID !== null ? candidates.filter(candidate => isColonyRimHex(board, candidate, colonyID)) : candidates;

  return new HexGroup(rim.filter(target => getPlacementTransforms(board, colony, target, colonyID).length !== 0));
};
